<template>
  <card class="card" title="Mon Profil">
    <div>
      <form @submit.prevent="saveProfile">
        <div v-if="loading" class="text-center">
          <p>Chargement...</p>
        </div>

        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <div v-if="!loading">
          <div class="row">
            <div class="col-md-3">
              <fg-input type="text" label="Nom d'utilisateur" placeholder="Username" v-model="user.username" readonly />
            </div>
            <div class="col-md-5">
              <fg-input type="email" label="Email" placeholder="Email" v-model="user.email" required />
            </div>
            <div class="col-md-4">
              <fg-input type="text" label="Adresse" placeholder="Adresse" v-model="user.adresse" />
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <fg-input type="text" label="Nom" placeholder="Last Name" v-model="user.last_name" required />
            </div>
            <div class="col-md-4">
              <fg-input type="text" label="Prénom(s)" placeholder="First Name" v-model="user.first_name" required />
            </div>
            <div class="col-md-4">
              <fg-input type="text" label="Fonction" placeholder="Fonction" v-model="user.fonction" />
            </div>
          </div>
          
          <div class="row" v-if="showClientFields">
            <div class="col-md-4">
              <fg-input type="date" label="Date de naissance" placeholder="Date de naissance"
                v-model="client.date_born" />
            </div>
            <div class="col-md-4">
              <fg-input type="text" label="Commune de naissance" placeholder="Commune de naissance"
                v-model="client.commune_born" />
            </div>
            <div class="col-md-4">
              <fg-input type="text" label="Département de naissance" placeholder="Département de naissance"
                v-model="client.departement_born" />
            </div>
          </div>
          
          <div class="row">
            <div class="col-md-4">
              <fg-input type="text" label="Ville" placeholder="Ville" v-model="user.ville" />
            </div>
            <div class="col-md-4">
              <fg-input type="text" label="Pays" placeholder="Pays" v-model="user.pays" />
            </div>
            <div class="col-md-4">
              <fg-input type="number" label="Téléphone" placeholder="Téléphone" v-model="user.tel" />
            </div>
          </div>

          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <div class="text-center">
            <p-button type="info" round native-type="submit" :loading="saving">
              {{ saving ? "Enregistrement..." : "Modifier" }}
            </p-button>
          </div>
        </div>
        <div class="clearfix"></div>
      </form>
    </div>
  </card>
</template>

<script>
import axios from 'axios';
import { safeErrorMessage } from '@/utils/errorMessages';
import { getUserRole, isClientFacingRole, refreshProfileFromApi } from '@/utils/auth';
import { clearAuthenticatedSession, isAuthenticatedSession } from '@/utils/sessionAuth';

export default {
  data() {
    return {
      profileRevision: 0,
      user: {
        username: '',
        email: '',
        adresse: '',
        last_name: '',
        first_name: '',
        fonction: '',
        ville: '',
        pays: '',
        tel: ''
      },
      client: {
        date_born: '',
        commune_born: '',
        departement_born: ''
      },
      error: null,
      errorMessage: null,
      successMessage: null,
      loading: true,
      saving: false
    };
  },
  computed: {
    userRole() {
      void this.profileRevision;
      return getUserRole() || '';
    },
    showClientFields() {
      return isClientFacingRole(this.userRole);
    },
  },
  async mounted() {
    await refreshProfileFromApi();
    this.profileRevision += 1;
    await this.fetchUserData();
    if (this.showClientFields) {
      await this.fetchClientData();
    }
  },
  methods: {
    async fetchUserData() {
      try {
        if (!isAuthenticatedSession()) {
          this.error = "Token non trouvé, veuillez vous connecter.";
          this.$router.push("/connexion");
          this.loading = false;
          return;
        }

        const response = await axios.get("/api/profile/", {
          withCredentials: true,
        });

        this.user = response.data;
      } catch (err) {
        console.error("Erreur lors du chargement du profil:", err);
        if (err.response && err.response.status === 401) {
          this.error = "Session expirée, veuillez vous reconnecter.";
          clearAuthenticatedSession();
          this.$router.push("/connexion");
        } else {
          this.error = "Impossible de charger les informations du profil.";
        }
      } finally {
        this.loading = false;
      }
    },
    
    async fetchClientData() {
      try {
        if (!isAuthenticatedSession()) return;

        const response = await axios.get("/api/all-clients/", {
          withCredentials: true,
        });
        
        if (response.data) {
          this.client = response.data;
        }
      } catch (err) {
        console.error("Erreur lors du chargement des informations du client:", err);
      }
    },

    async saveProfile() {
      this.saving = true;
      this.errorMessage = null;
      this.successMessage = null;

      try {
        // Mise à jour du profil utilisateur
        await axios.put(
          "/api/profile/",
          {
            username: this.user.username,
            email: this.user.email,
            last_name: this.user.last_name,
            first_name: this.user.first_name,
            ville: this.user.ville,
            pays: this.user.pays,
            tel: this.user.tel,
            adresse: this.user.adresse,
            fonction: this.user.fonction,
          },
          {
            withCredentials: true
          }
        );

        // Mise à jour du profil client si nécessaire
        if (this.showClientFields) {
          await axios.put(
            "/api/all-clients/",
            {
              date_born: this.client.date_born,
              commune_born: this.client.commune_born,
              departement_born: this.client.departement_born,
            },
            {
              withCredentials: true
            }
          );
        }

        this.successMessage = "Profil mis à jour avec succès!";
      } catch (error) {
        // safeErrorMessage : jamais le JSON brut de la réponse serveur (l'ancien
        // code affichait littéralement JSON.stringify(error.response.data)).
        this.errorMessage = safeErrorMessage(error, "Impossible d'enregistrer les modifications du profil.");
        this.successMessage = null;
      } finally {
        this.saving = false;
      }
    },
  }
};
</script>
